"use client";

import React from "react";
import { ArrowRight, Quote, Linkedin, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const leaders = [{
  name: "Gavin McDonald",
  role: "Managing Partner",
  image: "https://cyradoncapital.com/wp-content/uploads/2026/03/Linkedin.jpeg?v=fnbgujndqto",
  bio: "Gavin McDonald is a Charlotte-metro based wealth strategist serving clients across the Southeast, with a specialized focus on wealth building, asset protection, real estate investors, and small business owners seeking advanced tax strategies and risk management.\n\n\nWith a background in banking and financial analytics, Gavin brings a disciplined, data-driven approach to comprehensive financial planning. His advisory philosophy integrates tax efficiency, asset protection, retirement design, insurance optimization, and multi-generational wealth transfer into a cohesive strategy tailored to each client’s financial architecture.\n\n\nGavin works collaboratively with a trusted network of CPAs, financial advisors, and legal professionals — including New South Wealth Management — to deliver coordinated planning strategies. Through these professional relationships, clients benefit from integrated tax planning, estate structuring, business succession design, and advanced asset protection strategies. This team-based model ensures that investment strategy, tax positioning, and legal structuring are aligned under one comprehensive framework.",
  philosophy: "True wealth architecture isn't just about returns; it's about the intelligent structuring of assets to minimize tax exposure and ensure generational transfer. We treat every client's portfolio with the rigor of a corporate balance sheet.",
  credentials: ["CFA® Charterholder", "Certified Financial Planner™"],
  contact: {
    email: "michael.j@cyradoncapital.com",
    linkedin: "#"
  }
}];
export default function LeadershipInsights() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="leadership-insights" ref={ref} className={`dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 text-center md:text-left">
          <h2 className="text-3xl md:text-4xl font-serif font-light tracking-tight mb-4 text-primary">
            Leadership Insights
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg font-light">With a team based model approach, Gavin brings institutional expertise to personal wealth management.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {leaders.map((leader, i) => <div key={leader.name} data-index={i} className="group">
              <Card className="h-full border-border bg-card hover:border-primary/30 transition-colors duration-300 overflow-hidden flex flex-col">
                <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
                  <img src={leader.image} alt={leader.name} className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                </div>
                <CardHeader className="pt-6 pb-2">
                  <CardTitle className="text-xl font-serif tracking-wide text-card-foreground">
                    {leader.name}
                  </CardTitle>
                  <CardDescription className="text-primary font-medium uppercase tracking-wider text-xs">
                    {leader.role}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-4 flex-grow">
                  <p className="text-muted-foreground text-sm line-clamp-3 leading-relaxed">
                    {leader.bio}
                  </p>
                </CardContent>
                <CardFooter className="pt-0 pb-6">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="w-full border-primary/20 hover:bg-primary hover:text-primary-foreground group-hover:border-primary/50 transition-all">
                        View Full Bio
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent className="dark bg-background border-l border-border w-full sm:max-w-md md:max-w-lg overflow-y-auto">
                      <SheetHeader className="mb-8 mt-6">
                        <div className="w-24 h-24 mb-6 overflow-hidden rounded-full border-2 border-primary/20">
                          <img src={leader.image} alt={leader.name} className="w-full h-full object-cover grayscale" />
                        </div>
                        <SheetTitle className="text-3xl font-serif text-foreground">
                          {leader.name}
                        </SheetTitle>
                        <SheetDescription className="text-primary text-sm font-medium uppercase tracking-widest">
                          {leader.role}
                        </SheetDescription>
                      </SheetHeader>

                      <div className="space-y-8">
                        <div>
                          <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-3 border-b border-border pb-2">
                            Professional Background
                          </h4>
                          <p className="text-muted-foreground leading-relaxed">
                            {leader.bio}
                          </p>
                        </div>

                        <div className="bg-muted/30 p-6 rounded-lg border border-border/50">
                          <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground uppercase tracking-wider mb-3">
                            <Quote className="h-4 w-4 text-primary" />
                            Investment Philosophy
                          </h4>
                          <p className="text-muted-foreground italic leading-relaxed">
                            "{leader.philosophy}"
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-3 border-b border-border pb-2">
                            Credentials
                          </h4>
                          
                        </div>

                        <div className="pt-4 flex gap-4">
                          <Button variant="outline" size="sm" className="flex-1 gap-2">
                            <Mail className="h-4 w-4" />
                            Contact
                          </Button>
                          <Button variant="outline" size="sm" className="flex-1 gap-2">
                            <Linkedin className="h-4 w-4" />
                            LinkedIn
                          </Button>
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}